import { useState } from "react";
import { Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../../lib/api";
import UnitSelect from "../review/UnitSelect";

// Owner correction of the billed unit on one price-history row (BOX typed where the supplier billed EACH). Only the unit and
// derived packaging change — quantity, price, totals never do. The product's BOX / EACH prices are re-derived afterwards.
export default function BilledUnitCell({ row, index, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [unit, setUnit] = useState(row.unit || "");
  const [saving, setSaving] = useState(false);
  const label = <>{row.unit || "—"}{row.pack_size ? ` · ${row.pack_size}` : ""}</>;
  if (!canEdit || row.source !== "invoice" || !row.line_id) return <span>{label}</span>;
  const save = async () => {
    if (!unit.trim() || unit.trim().toLowerCase() === (row.unit || "").toLowerCase()) { setEditing(false); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/invoice-lines/${row.line_id}/billed-unit`, { unit: unit.trim() });
      toast.success(`Billed unit corrected to ${data.unit.toUpperCase()} — prices and quantities unchanged, ${data.unit.toUpperCase()} and ${(row.unit || "").toUpperCase()} prices re-derived separately`);
      if (data.lane_defined === false) toast.message(`No confirmed ${data.unit.toUpperCase()} packaging yet — confirm what one ${data.unit.toUpperCase()} contains in the “Also bought as” card above so future invoices reuse it.`);
      setEditing(false);
      onSaved?.(data);
    } catch (e) { toast.error(errorMessage(e)); } finally { setSaving(false); }
  };
  if (!editing) {
    return (
      <button type="button" onClick={() => { setUnit(row.unit || ""); setEditing(true); }} className="inline-flex items-center gap-1 hover:text-emerald-700" title="Correct the billed unit" data-testid={`price-history-billed-edit-${index}`}>
        {label}{row.billed_unit_corrected && <span className="text-[9px] rounded bg-emerald-50 text-emerald-700 px-1 normal-case" data-testid={`price-history-billed-corrected-${index}`}>corrected</span>}<Pencil className="h-3 w-3 text-slate-400" />
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 normal-case" data-testid={`price-history-billed-form-${index}`}>
      <UnitSelect value={unit} onChange={setUnit} testId={`price-history-billed-unit-${index}`} className="w-28" />
      <button type="button" onClick={save} disabled={saving} className="rounded bg-emerald-600 p-1 text-white hover:bg-emerald-700 disabled:opacity-50" data-testid={`price-history-billed-save-${index}`}><Check className="h-3.5 w-3.5" /></button>
      <button type="button" onClick={() => setEditing(false)} disabled={saving} className="rounded p-1 text-slate-500 hover:bg-slate-100" data-testid={`price-history-billed-cancel-${index}`}><X className="h-3.5 w-3.5" /></button>
    </span>
  );
}
